<template>
    <div class="new-style vrm-date-time-picker">
        <vrm-icon-button
            icon-class="sg-vrm-icon_048_arrow-left-16px"
            class="vrm-date-time-picker__arrow-button"
            @click="shiftRange('left')"
        />

        <input
            v-if="!isDynamicEssPicker"
            type="text"
            readonly
            class="vrm-date-time-picker__input"
            data-cy="date-picker"
            :value="inputText"
            :size="inputText.length + 5"
            @click.stop="toggleDropdown(!isDropdownOpen)"
        />
        <div v-else class="vrm-date-time-picker__day">
            <div
                :class="[
                    'vrm-date-time-picker__dropdown__custom__inputs',
                    { 'flex-grow': isDynamicEssPicker },
                ]"
            >
                <input
                    :id="isDynamicEssPicker ? 'dpMinDess' : 'dpMin'"
                    :class="{
                        'vrm-date-time-picker__dropdown__custom-date': !isDynamicEssPicker,
                        'dess-day-picker': isDynamicEssPicker,
                    }"
                    data-cy="day-picker"
                    type="text"
                />
            </div>
        </div>

        <vrm-icon-button
            icon-class="sg-vrm-icon_049_arrow-right-16px"
            class="vrm-date-time-picker__arrow-button"
            :class="{ 'sg-disabled': !canShiftRight }"
            @click="canShiftRight && shiftRange('right')"
        ></vrm-icon-button>

        <vrm-dropdown-menu
            :is-open="isDropdownOpen && !isDynamicEssPicker"
            custom-class="vrm-date-time-picker__dropdown"
            class="vrm-date-time-picker__dropdown"
            data-cy="date-picker-modal"
            @close="toggleDropdown(false)"
        >
            <div class="vrm-date-time-picker__dropdown__close-button">
                <vrm-icon-button
                    icon-class="sg-vrm-icon_055_cross-16px"
                    @click="toggleDropdown(false)"
                ></vrm-icon-button>
            </div>
            <div class="vrm-date-time-picker__dropdown__quick-ranges">
                <h2>
                    {{
                        $t('datepicker.quick_ranges', {
                            i18nFallback: 'Quick Ranges',
                        })
                    }}
                </h2>
                <div class="vrm-date-time-picker__dropdown__quick-ranges__groups">
                    <span
                        v-for="(quickRangeGroup, index) in quickRanges.slice(0, -1)"
                        :key="'qrGroup' + index"
                        class="vrm-date-time-picker__dropdown__quick-ranges__group"
                    >
                        <span
                            v-for="(quickRange, idx) in quickRangeGroup"
                            :key="'qrGroup' + index + '_' + idx"
                            class="vrm-date-time-picker__dropdown__quick-ranges__range"
                            :class="{
                                active: selectedRange && quickRange.name === selectedRange.name,
                            }"
                            @click="selectRangeByName(quickRange)"
                        >
                            {{
                                $t('datepicker.' + quickRange.name, {
                                    i18nFallback: getFallbackTranslation(quickRange.name),
                                })
                            }}
                        </span>
                    </span>
                </div>
                <div class="vrm-date-time-picker__dropdown__quick-ranges__future-title">
                    {{
                        $t('datepicker.future', {
                            i18nFallback: 'Future',
                        })
                    }}
                </div>
                <div class="vrm-date-time-picker__dropdown__quick-ranges__future-group">
                    <span
                        v-for="(quickRange, idx) in quickRanges.slice(-1)[0]"
                        :key="'qrFutureGroup_' + idx"
                        class="vrm-date-time-picker__dropdown__quick-ranges__future-group__range"
                        :class="{
                            active: selectedRange && quickRange.name === selectedRange.name,
                        }"
                        @click="selectRangeByName(quickRange)"
                    >
                        <span>
                            {{
                                $t('datepicker.' + quickRange.name, {
                                    i18nFallback: getFallbackTranslation(quickRange.name),
                                })
                            }}
                        </span>
                    </span>
                </div>
            </div>

            <div class="vrm-date-time-picker__dropdown__separator" />

            <div class="vrm-date-time-picker__dropdown__custom">
                <h2>
                    {{
                        $t('datepicker.custom', {
                            i18nFallback: 'Custom',
                        })
                    }}
                </h2>
                <div class="sg-textfield-label">
                    {{
                        $t('datepicker.from', {
                            i18nFallback: 'From',
                        })
                    }}
                </div>
                <div class="vrm-date-time-picker__dropdown__custom__inputs">
                    <input
                        :id="isDynamicEssPicker ? 'dpMinDess' : 'dpMin'"
                        class="vrm-date-time-picker__dropdown__custom-date"
                        type="text"
                    />
                    <vrm-time-input
                        v-if="!isDynamicEssPicker"
                        v-model="selectedMinTime"
                        :max-time="maxMinTime"
                        unique-key-string="min"
                    />
                </div>
                <div class="sg-textfield-label">
                    {{
                        $t('datepicker.to', {
                            i18nFallback: 'To',
                        })
                    }}
                </div>
                <div
                    class="vrm-date-time-picker__dropdown__custom__inputs"
                    :class="{ 'error-alert': alertInput }"
                >
                    <input
                        :id="isDynamicEssPicker ? 'dpMaxDess' : 'dpMax'"
                        class="vrm-date-time-picker__dropdown__custom-date"
                        type="text"
                    />
                    <vrm-time-input
                        v-if="!isDynamicEssPicker"
                        v-model="selectedMaxTime"
                        :max-time="maxMaxTime"
                        unique-key-string="max"
                    />
                </div>
                <span v-if="alertInput" class="sg-input-error">
                    {{
                        this.$t('datepicker.invalid_datetime_range', {
                            i18nFallback: 'Date and time range is invalid',
                        })
                    }}
                </span>
            </div>
        </vrm-dropdown-menu>
    </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import VrmIconButton from '../../molecules/VrmIconButton';
import VrmDropdownMenu from './VrmDropdownMenu';
import {
    findRangeByName,
    toSiteTime,
    DASHBOARD_RANGES,
    DESS_RANGES,
    HOUR_IN_SECONDS,
    DAY_IN_SECONDS,
    computePrecision,
    roundTimestamp,
    toRangeTimestamp,
    findRangeByDates,
    getFallbackTranslation,
    LOCALE_DICT,
} from './utils';
import moment from 'moment/moment';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import {
    NOTIFICATION_TYPE_ERROR,
    NOTIFICATION_TYPE_WARNING,
} from '@/vue/store/modules/notifications';
import VrmTimeInput from '@/vue/components/shared/date-time-picker/VrmTimeInput.vue';

@Component({
    components: { VrmTimeInput, VrmIconButton, VrmDropdownMenu },
})
@AngularLocaleBundle('datepicker')
@AngularDirective('vrm-date-time-picker')
export default class VrmDateTimePicker extends Vue {
    @ngInject()
    $rootScope;
    @ngInject()
    localStorageService;
    @ngInject()
    $stateParams;
    @ngInject()
    InstallationsService;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    UserService;
    @ngInject()
    VrmLangService;
    @Prop({ type: String, default: 'date_picker' })
    localStorageKey;
    @Prop({ type: Boolean, default: false })
    isDynamicEssPicker;
    @Prop({ type: Object, default: () => {} })
    control;
    @Prop({ type: Object, default: () => {} })
    range;

    installation;
    isDropdownOpen = false;
    storageLoaded = false;

    dpMin = null;
    dpMax = null;
    siteUTCOffset = 0;
    lastShiftUnit = null;
    lastShiftValue = null;
    selectedRange = false;
    isCustom = false;
    datesBeforeSelect = { min: null, max: null };
    getFallbackTranslation = getFallbackTranslation;
    alertInput = false;
    get quickRanges() {
        return this.isDynamicEssPicker ? DESS_RANGES : DASHBOARD_RANGES;
    }

    getMaxDate() {
        const currentSiteTime = toSiteTime(new Date(), this.siteUTCOffset);
        return moment(currentSiteTime)
            .add(168, 'hours')
            .toDate(); // max (solar) forecast available
    }

    get startDate() {
        return this.dpMin.selectedDates[0];
    }

    get endDate() {
        return this.dpMax.selectedDates[0];
    }

    set startDate(date) {
        this.dpMin.selectDate(date, { silent: true });
    }

    set endDate(date) {
        this.dpMax.selectDate(date, { silent: true });
    }

    get inputText() {
        if (this.selectedRange) {
            return this.$t('datepicker.' + this.selectedRange.name, {
                i18nFallback: getFallbackTranslation(this.selectedRange.name),
            });
        }
        if (this.isDynamicEssPicker) {
            return moment(this.startDate).format('LL');
        }
        const format =
            this.$t('datepicker.format', {
                i18nFallback: 'yyyy-mm-dd',
            }).toUpperCase() + ' HH:mm';
        const to = this.$t('datepicker.to', {
            i18nFallback: 'To',
        }).toLowerCase();
        return (
            moment(this.startDate).format(format) +
            ' ' +
            to +
            ' ' +
            moment(this.endDate).format(format)
        );
    }

    dessDateFormat(date) {
        if (this.isDynamicEssPicker && this.selectedRange) {
            // use shorter 'Day after' translation instead of 'Day after tomorrow'
            const selectedRangeName =
                this.selectedRange.name === 'qr_day_after_tomorrow'
                    ? 'qr_day_after'
                    : this.selectedRange.name;
            return this.$t('datepicker.' + selectedRangeName, {
                i18nFallback: getFallbackTranslation(selectedRangeName),
            });
        }
        const format = this.$t('datepicker.format', {
            i18nFallback: 'yyyy-mm-dd',
        }).toUpperCase();
        return moment(date).format(format);
    }

    // compute the interval to be shown as the tick gap in the charts
    get computedInterval() {
        const diff = (this.endDate - this.startDate) / 1000;
        let interval = '15mins';
        interval = diff >= 3 * HOUR_IN_SECONDS ? 'hours' : interval;
        interval = diff > 2 * DAY_IN_SECONDS ? 'days' : interval;
        interval = diff > DAY_IN_SECONDS * 31 + HOUR_IN_SECONDS ? 'weeks' : interval; // months can have 31 days + 1 hour due to daylight savings
        interval = diff > DAY_IN_SECONDS * 31 * 3 ? 'months' : interval;
        interval = diff > DAY_IN_SECONDS * 31 * 12 * 3 ? 'years' : interval;
        return interval;
    }

    // the interval to reload should be one smaller than that shown as tick gap
    get reloadInterval() {
        const order = ['15mins', 'hours', 'days', 'weeks', 'months', 'years'];
        const orderPos = order.indexOf(this.computedInterval);
        const reloadPos = orderPos > 0 ? orderPos - 1 : 0;
        return order[reloadPos];
    }

    @Watch('installation.timezone_offset')
    setOffset() {
        if (!this.installation || !this.installation.timezone_offset) return;
        this.siteUTCOffset = this.installation.timezone_offset * 1000;
        this.refreshRange();
    }

    loadLocalStorage() {
        this.storageLoaded = true;
        let key = 'installation.' + this.installation.id + '.' + this.localStorageKey;
        let settings = this.localStorageService.get(key);
        if (!settings) {
            this.selectRangeByName(this.selectedRange, this.isDynamicEssPicker);
            return;
        }
        const cachedRange = findRangeByName(settings.selectedRange, this.isDynamicEssPicker);
        if (cachedRange) {
            this.selectRangeByName(cachedRange, false);
            return;
        }
        if (!settings.startDate || !settings.endDate) return;
        if (this.isDynamicEssPicker) {
            settings.startDate = moment(settings.startDate)
                .startOf('day')
                .toDate();
            settings.endDate = moment(settings.startDate)
                .endOf('day')
                .toDate();
        }
        this.selectRangeByDates(settings.startDate, settings.endDate);
    }

    saveToLocalStorage() {
        if (!this.installation || !this.storageLoaded) return;
        let key = 'installation.' + this.installation.id + '.' + this.localStorageKey;
        this.localStorageService.set(key, {
            selectedRange: this.selectedRange.name,
            startDate: this.startDate,
            endDate: this.endDate,
        });
    }

    // pre-set the datepicker to 'today'
    // (will get overwritten by local storage data in mounted if it exists)
    created() {
        this.selectedRange = findRangeByName('qr_today', this.isDynamicEssPicker);
    }

    get isSharedSite() {
        return this.$rootScope.userState.isShare;
    }

    async mounted() {
        let self = this;
        let locale;
        await this.VrmLangService.getLang().then(language => {
            locale = LOCALE_DICT[language];
        });
        if (!this.isSharedSite) await this.UserService.getUser();
        const userPreferredStartOfWeek =
            !this.isSharedSite && this.UserService.user.weekday === 'monday' ? 1 : 0;
        this.dpMin = new AirDatepicker(this.isDynamicEssPicker ? '#dpMinDess' : '#dpMin', {
            keyboardNav: false,
            firstDay: userPreferredStartOfWeek,
            locale: locale,
            dateFormat: date => this.dessDateFormat(date),
            startDate: new Date(),
            maxDate: this.getMaxDate(),
            selectedDates: [new Date()],
            onBeforeSelect({ date, datepicker }) {
                if (datepicker && datepicker.visible) {
                    const lastDate = datepicker.selectedDates[0];
                    const lastMoment = moment(lastDate);
                    const momentObj = moment(date).set({
                        hour: lastMoment.hour(),
                        minute: lastMoment.minute(),
                    });
                    if (momentObj.toDate() > self.getMaxDate()) {
                        self.datesBeforeSelect.min = self.getMaxDate();
                    } else {
                        self.datesBeforeSelect.min = lastDate;
                    }
                }
                return true;
            },
            onSelect({ date, datepicker }) {
                date.setSeconds(0, 0);
                self.updateMaxDates();
                if (datepicker.visible) self.handleDatePicking(datepicker, date);
            },
            onChangeViewDate({ month, year }) {
                if (
                    year > self.getMaxDate().getFullYear() ||
                    (year === self.getMaxDate().getFullYear() &&
                        month > self.getMaxDate().getMonth())
                ) {
                    self.dpMin.prev();
                }
            },
            onHide(isFinished) {
                if (!isFinished) return;
                self.inputChange(self.dpMin.$el.value, self.dpMin);
            },
        });
        this.dpMax = new AirDatepicker(this.isDynamicEssPicker ? '#dpMaxDess' : '#dpMax', {
            keyboardNav: false,
            firstDay: userPreferredStartOfWeek,
            locale: locale,
            dateFormat: this.$t('datepicker.format', {
                i18nFallback: 'yyyy-mm-dd',
            }).replaceAll('m', 'M'),
            startDate: new Date(),
            maxDate: this.getMaxDate(),
            selectedDates: [new Date()],
            onBeforeSelect({ date, datepicker }) {
                if (datepicker && datepicker.visible) {
                    const lastDate = datepicker.selectedDates[0];
                    const lastMoment = moment(lastDate);
                    const momentObj = moment(date).set({
                        hour: lastMoment.hour(),
                        minute: lastMoment.minute(),
                    });
                    if (momentObj.toDate() > self.getMaxDate()) {
                        self.datesBeforeSelect.max = self.getMaxDate();
                    } else {
                        self.datesBeforeSelect.max = lastDate;
                    }
                }
                return true;
            },
            onSelect({ date, datepicker }) {
                date.setSeconds(0, 0);
                self.updateMaxDates();
                if (datepicker.visible) self.handleDatePicking(datepicker, date);
            },
            onChangeViewDate({ month, year }) {
                if (
                    year > self.getMaxDate().getFullYear() ||
                    (year === self.getMaxDate().getFullYear() &&
                        month > self.getMaxDate().getMonth())
                ) {
                    self.dpMax.prev();
                }
            },
            onHide(isFinished) {
                if (!isFinished) return;
                self.inputChange(self.dpMax.$el.value, self.dpMax);
            },
        });

        // pass the function to refresh the range to the parent
        this.control.refreshRange = this.refreshRange;

        await this.InstallationsService.getInstallation(this.$stateParams.id).then(
            loadedSite => (this.installation = loadedSite),
        );
        this.setOffset();
        this.loadLocalStorage();
    }

    // check used to disable right button
    get canShiftRight() {
        if (!this.dpMax || !this.dpMin) return false;
        if (this.isDynamicEssPicker) {
            return (
                moment(this.endDate)
                    .add(1, 'day')
                    .startOf('day')
                    .toDate() <= this.getMaxDate()
            );
        }
        // can shift if there is a quickrange
        const range = this.selectedRange;
        if (range && range.next) {
            return true;
        }

        // calculate what the result of a shift to the right would be
        let shiftValue =
            range && range.shiftValue
                ? range.shiftValue
                : this.lastShiftValue
                ? this.lastShiftValue
                : null;
        let shiftUnit =
            range && range.shiftUnit
                ? range.shiftUnit
                : this.lastShiftUnit
                ? this.lastShiftUnit
                : null;
        let shiftedStart;
        let shiftedEnd;
        if (shiftValue && shiftUnit) {
            shiftedStart = moment(this.startDate).add(shiftValue, shiftUnit);
            shiftedEnd = moment.min(
                moment(this.endDate).add(shiftValue, shiftUnit),
                moment(this.getMaxDate()),
            );
        } else {
            const duration = Math.abs(this.endDate - this.startDate);
            shiftedStart = moment(this.startDate).add(duration, 'ms');
            shiftedEnd = moment.min(
                moment(this.endDate).add(duration, 'ms'),
                moment(this.getMaxDate()),
            );
        }

        // only allow shifting right if the resulting range would be at least an hour
        return shiftedEnd - shiftedStart > HOUR_IN_SECONDS * 1000;
    }

    shiftRange(direction) {
        const range = this.selectedRange;
        if (range) {
            // Check if next or previous range is already specified by a quickrange
            if (direction === 'right' && range.next) {
                this.selectRangeByName(findRangeByName(range.next, this.isDynamicEssPicker));
                return;
            } else if (direction === 'left' && range.prev) {
                this.selectRangeByName(findRangeByName(range.prev, this.isDynamicEssPicker));
                return;
            }

            // if the shiftValue and shiftUnit exist on the range set them
            if (range.shiftValue && range.shiftUnit) {
                this.lastShiftValue = range.shiftValue;
                this.lastShiftUnit = range.shiftUnit;
            }
        }

        if (this.isDynamicEssPicker) {
            const shiftedStart = (direction === 'right'
                ? moment(this.startDate)
                      .add(1, 'day')
                      .startOf('day')
                : moment(this.startDate)
                      .subtract(1, 'day')
                      .startOf('day')
            ).toDate();
            const shiftedEnd = (direction === 'right'
                ? moment(this.endDate)
                      .add(1, 'day')
                      .endOf('day')
                : moment(this.endDate)
                      .subtract(1, 'day')
                      .endOf('day')
            ).toDate();

            this.selectRangeByDates(shiftedStart, shiftedEnd);
            return;
        }

        if (this.lastShiftValue && this.lastShiftUnit) {
            const shiftedStart = (direction === 'right'
                ? moment(this.startDate).add(this.lastShiftValue, this.lastShiftUnit)
                : moment(this.startDate).subtract(this.lastShiftValue, this.lastShiftUnit)
            ).toDate();
            const shiftedEnd = (direction === 'right'
                ? moment.min(
                      moment(this.endDate).add(this.lastShiftValue, this.lastShiftUnit),
                      moment(this.getMaxDate()),
                  )
                : moment(this.endDate).subtract(this.lastShiftValue, this.lastShiftUnit)
            ).toDate();
            this.selectRangeByDates(shiftedStart, shiftedEnd);
            return;
        }

        // last case for custom ranges
        const duration = Math.abs(this.endDate - this.startDate);
        this.startDate = (direction === 'right'
            ? moment(this.startDate).add(duration, 'ms')
            : moment(this.startDate).subtract(duration, 'ms')
        ).toDate();
        this.endDate = (direction === 'right'
            ? moment.min(moment(this.endDate).add(duration, 'ms'), moment(this.getMaxDate()))
            : moment(this.endDate).subtract(duration, 'ms')
        ).toDate();
        this.updateRangeProp();
    }

    selectRangeByName(quickRange, closeDropdownOnSelect = true) {
        this.selectedRange = quickRange;

        const userWeekStart = !this.isSharedSite ? this.UserService.user.weekday : 'monday';
        if (quickRange) {
            const currentSiteTime = toSiteTime(new Date(), this.siteUTCOffset);
            // if the quick range is a week range, adjust the start date based on the user's preferred start day
            if (quickRange.name === 'qr_this_week' || quickRange.name === 'qr_previous_week') {
                this.startDate = this.selectedRange.startDate(currentSiteTime, userWeekStart);
                this.endDate = this.selectedRange.endDate(currentSiteTime, userWeekStart);
            } else {
                this.startDate = this.selectedRange.startDate(currentSiteTime);
                this.endDate = this.selectedRange.endDate(currentSiteTime);
            }
        }
        this.updateRangeProp();
        this.saveToLocalStorage();

        if (closeDropdownOnSelect) this.toggleDropdown(false);
        this.validateDateTimeRange();
    }

    selectRangeByDates(startDate, endDate, resetLastShiftData = false) {
        // first check if there is a potential quick range with the same dates
        const foundQuickRange = findRangeByDates(
            startDate,
            endDate,
            this.siteUTCOffset,
            this.isDynamicEssPicker,
            this.isSharedSite ? 'monday' : this.UserService.user.weekday,
        );
        if (foundQuickRange) {
            this.selectRangeByName(foundQuickRange, false);
        } else {
            // if there isn't set it to custom
            this.startDate = startDate;
            this.endDate = endDate;
            this.isCustom = true;
            this.selectedRange = false;
            if (resetLastShiftData) {
                this.lastShiftValue = null;
                this.lastShiftUnit = null;
            }
            this.updateRangeProp();
        }
        this.saveToLocalStorage();

        this.validateDateTimeRange();
    }

    refreshRange() {
        if (!this.dpMax || !this.dpMin) return false;
        this.selectRangeByName(this.selectedRange, false);
        this.updateMaxDates();
    }

    updateMaxDates() {
        const maxDate = this.getMaxDate();
        this.dpMax.update({
            maxDate: maxDate,
        });
        this.dpMin.update({
            maxDate: maxDate,
        });
    }

    // update the range prop to pass data to the parent
    updateRangeProp() {
        if (this.selectedRange) {
            this.range.name = this.selectedRange.name;
            this.range.endsNow = !!this.selectedRange.endsNow;
            this.range.keepRealtime = !!this.selectedRange.keepRealtime;
            this.range.interval = this.selectedRange.interval
                ? this.selectedRange.interval
                : this.computedInterval;
        } else {
            this.range.name = '';
            this.range.endsNow =
                Math.abs(toSiteTime(new Date(), this.siteUTCOffset) - this.endDate) < 60 * 1000;
            const duration = (this.endDate - this.startDate) / 1000;
            this.range.keepRealtime =
                this.startDate < new Date() &&
                new Date() < this.endDate &&
                duration > 0 &&
                duration <= DAY_IN_SECONDS;
            this.range.interval = this.computedInterval;
        }
        this.range.startDate = toRangeTimestamp(
            this.startDate,
            this.siteUTCOffset,
            this.installation.timezone,
        );
        this.range.endDate =
            toRangeTimestamp(this.endDate, this.siteUTCOffset, this.installation.timezone) - 1;
        this.range.precision = computePrecision(this.range.interval);
        this.range.reloadInterval = this.reloadInterval;
        this.range.roundedStartDate = roundTimestamp(
            this.startDate.getTime(),
            this.computedInterval,
            this.siteUTCOffset,
            this.installation.timezone,
        );
        this.range.roundedEndDate = roundTimestamp(
            this.endDate,
            this.computedInterval,
            this.siteUTCOffset,
            this.installation.timezone,
        );
        this.range.reloadStartDate = roundTimestamp(
            this.startDate,
            this.reloadInterval,
            this.siteUTCOffset,
            this.installation.timezone,
        );
        this.range.reloadEndDate = roundTimestamp(
            this.endDate,
            this.reloadInterval,
            this.siteUTCOffset,
            this.installation.timezone,
        );
    }
    handleDatePicking(picker, date, includeTime = false) {
        if (picker.visible) picker.hide();
        const newDate = moment(date);
        if (!includeTime) {
            const previousMoment = moment(
                this.datesBeforeSelect[picker === this.dpMin ? 'min' : 'max'],
            );
            newDate.set({
                hour: previousMoment.hour(),
                minute: previousMoment.minute(),
                second: 0,
                millisecond: 0,
            });
        }
        if (this.isDynamicEssPicker) {
            this.selectRangeByDates(
                newDate.toDate(),
                moment(newDate)
                    .add(24, 'hours')
                    .subtract(1, 'seconds')
                    .toDate(),
                true,
            );
        } else if (picker === this.dpMin) {
            this.selectRangeByDates(newDate.toDate(), this.endDate, true);
        } else {
            this.selectRangeByDates(this.startDate, newDate.toDate(), true);
        }

        this.validateDateTimeRange();
    }

    inputChange(stringValue, picker) {
        let momentObject = null;

        if (this.isDynamicEssPicker) {
            switch (stringValue.toUpperCase()) {
                case '':
                case 'TODAY':
                case this.$t('datepicker.qr_today').toUpperCase():
                    momentObject = moment().startOf('day');
                    break;
                case 'YESTERDAY':
                case this.$t('datepicker.qr_yesterday').toUpperCase():
                    momentObject = moment()
                        .startOf('day')
                        .subtract(1, 'days');
                    break;
                case 'TOMORROW':
                case this.$t('datepicker.qr_tomorrow').toUpperCase():
                    momentObject = moment()
                        .startOf('day')
                        .add(1, 'days');
                    break;
                case 'DAY BEFORE':
                case 'DAY BEFORE YESTERDAY':
                case this.$t('datepicker.qr_day_before').toUpperCase():
                    momentObject = moment()
                        .startOf('day')
                        .subtract(2, 'days');
                    break;
                case 'DAY AFTER':
                case 'DAY AFTER TOMORROW':
                case this.$t('datepicker.qr_day_after').toUpperCase():
                case this.$t('datepicker.qr_day_after_tomorrow').toUpperCase():
                    momentObject = moment()
                        .startOf('day')
                        .add(2, 'days');
                    break;
            }
        }
        if (momentObject === null) {
            const allowedFormats = [
                'D/M/YYYY',
                'D-M-YYYY',
                'D.M.YYYY',
                'YYYY/M/D',
                'YYYY-M-D',
                'YYYY.M.D',
            ];
            momentObject = moment(stringValue, allowedFormats, true);
        }

        if (!momentObject.isValid()) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('datepicker.invalid_date_provided'),
            });
        } else {
            if (this.isDynamicEssPicker) {
                const maxDate = this.getMaxDate();
                if (momentObject.toDate() > maxDate) {
                    momentObject = moment(maxDate).startOf('day');
                }
            }
            this.handleDatePicking(picker, momentObject.toDate());
        }
    }

    toggleDropdown(newState) {
        if (this.dpMin.visible) this.dpMin.hide();
        if (this.dpMax.visible) this.dpMax.hide();
        this.isDropdownOpen = newState;
    }

    get selectedMinTime() {
        if (!this.dpMin) return '';
        const start = moment(this.startDate);
        return (
            String(start.hour()).padStart(2, '0') + ':' + String(start.minute()).padStart(2, '0')
        );
    }

    set selectedMinTime(timeString) {
        let momentObject = moment(timeString, 'H:m', true);
        if (!momentObject.isValid()) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('datepicker.invalid_time_provided'),
            });
        } else {
            let maxMomentObject = moment(this.maxMinTime, 'H:m', true);
            if (maxMomentObject < momentObject) {
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_WARNING,
                    message: this.$t('datepicker.max_time_exceeded'),
                });
                momentObject = maxMomentObject;
            }
            const start = moment(this.startDate);
            start.set({ hour: momentObject.hour(), minute: momentObject.minute(), second: 0 });
            this.handleDatePicking(this.dpMin, start.toDate(), true);
        }
    }

    get selectedMaxTime() {
        if (!this.dpMax) return '';
        const end = moment(this.endDate);
        return String(end.hour()).padStart(2, '0') + ':' + String(end.minute()).padStart(2, '0');
    }

    set selectedMaxTime(timeString) {
        let momentObject = moment(timeString, 'H:m', true);
        if (!momentObject.isValid()) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t('datepicker.invalid_time_provided'),
            });
        } else {
            let maxMomentObject = moment(this.maxMaxTime, 'H:m', true);
            if (maxMomentObject < momentObject) {
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_WARNING,
                    message: this.$t('datepicker.max_time_exceeded'),
                });
                momentObject = maxMomentObject;
            }
            const end = moment(this.endDate);
            end.set({ hour: momentObject.hour(), minute: momentObject.minute(), second: 0 });
            this.handleDatePicking(this.dpMax, end.toDate(), true);
        }
    }

    getMaxTime(date) {
        const max = this.getMaxDate();
        if (
            max.getFullYear() === date.getFullYear() &&
            max.getMonth() === date.getMonth() &&
            max.getDate() === date.getDate()
        ) {
            return (
                String(max.getHours()).padStart(2, '0') +
                ':' +
                String(max.getMinutes()).padStart(2, '0')
            );
        } else return '23:59';
    }

    get maxMaxTime() {
        if (!this.dpMax) return '23:59';
        return this.getMaxTime(this.endDate);
    }

    get maxMinTime() {
        if (!this.dpMin) return '23:59';
        return this.getMaxTime(this.startDate);
    }

    validateDateTimeRange() {
        this.alertInput = this.startDate >= this.endDate;
    }
}
</script>
